/* Finance pages -> Cilpronacc accounting API (through its gateway, /api/{service}/...).
   Config (optional):
     VITE_ACC_GATEWAY_URL  default https://acc.cilpron.com
     VITE_ACC_TOKEN_KEY    storage key holding the Cilpronacc JWT (falls back to the same keys the F&O client tries) */
import { getToken } from "./Api/Api";
import type { Binding, Row, Val } from "./Ops";

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
export const ACC_BASE = (env.VITE_ACC_GATEWAY_URL || "https://acc.cilpron.com").replace(/\/+$/, "");
const ACC_KEYS = [env.VITE_ACC_TOKEN_KEY, "acc_access_token", "cilpronacc_token"].filter((k): k is string => !!k);

type Rec = Record<string, unknown>;
type Vals = Record<string, Val>;

const accToken = (): string => {
  for (const store of [sessionStorage, localStorage]) {
    for (const k of ACC_KEYS) {
      try {
        const v = store.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
      } catch {
        /* storage blocked */
      }
    }
  }
  return getToken();
};

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = accToken();
  let res: Response;
  try {
    res = await fetch(`${ACC_BASE}/${path}`, {
      method,
      headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error(`Cannot reach the accounting API at ${ACC_BASE}.`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = text;
  }
  if (!res.ok) {
    const b = json as { title?: string; detail?: string; message?: string; errors?: Record<string, string[]> } | null;
    const first = b?.errors ? Object.entries(b.errors)[0] : undefined;
    throw new Error(first ? `${first[0]}: ${first[1][0]}` : b?.detail || b?.message || b?.title || (res.status === 401 ? "Not signed in to the accounting API." : res.status === 403 ? "No permission in the accounting API." : `Accounting API error (${res.status})`));
  }
  return json as T;
}

/** list endpoints return either a paged envelope or a plain array */
const items = (x: unknown): Rec[] => {
  if (Array.isArray(x)) return x as Rec[];
  const o = x as { items?: Rec[]; Items?: Rec[] } | null;
  return o?.items ?? o?.Items ?? [];
};
async function all(path: string, extra = ""): Promise<Rec[]> {
  const out: Rec[] = [];
  for (let p = 1; p <= 30; p++) {
    const r = await call<unknown>("GET", `${path}${path.includes("?") ? "&" : "?"}page=${p}&pageSize=200${extra}`);
    const list = items(r);
    out.push(...list);
    const tp = (r as { totalPages?: number } | null)?.totalPages;
    if (Array.isArray(r) || !tp || p >= tp) break;
  }
  return out;
}

const S = (x: unknown): string => (x === null || x === undefined ? "" : String(x));
const N = (x: unknown): number => Number(x) || 0;
const D = (x: unknown): string => S(x).slice(0, 10);
const today = (): string => new Date().toISOString().slice(0, 10);
const cap = (x: unknown): string => S(x).toLowerCase();
const pad = (k: number, w = 4): string => String(k).padStart(w, "0");

/** page account types: Asset / Liability / Equity / Revenue / Expense */
const acctType = (t: unknown): string => {
  const c = cap(t);
  if (c.includes("asset")) return "Asset";
  if (c.includes("liab")) return "Liability";
  if (c.includes("equity") || c.includes("capital")) return "Equity";
  if (c.includes("revenue") || c.includes("income") || c.includes("sales")) return "Revenue";
  if (c.includes("expense") || c.includes("cost")) return "Expense";
  return S(t);
};
const arr = async (path: string): Promise<Rec[]> => items(await call<unknown>("GET", path));

/* ---- lookups, re-read each time so they never go stale ---- */
const accountList = (): Promise<Rec[]> => arr("api/dashboard/ledger/accounts");
const acctId = async (code: unknown): Promise<number> => {
  const a = (await accountList()).find((x) => S(x.code) === S(code));
  if (!a) throw new Error(`Account ${S(code)} does not exist in the accounting system.`);
  return N(a.accountId);
};
const banksList = (): Promise<Rec[]> => arr("api/dashboard/lookups/banks");
const customerId = async (name: unknown): Promise<number> => {
  const c = (await all("api/receivables/customers")).find((x) => S(x.customerName) === S(name));
  if (!c) throw new Error(`Customer ${S(name)} does not exist in the accounting system.`);
  return N(c.customerId);
};
const vendorId = async (name: unknown): Promise<number> => {
  const c = (await all("api/payables/vendors")).find((x) => S(x.vendorName) === S(name));
  if (!c) throw new Error(`Vendor ${S(name)} does not exist in the accounting system.`);
  return N(c.vendorId);
};
const firstBank = async (): Promise<number> => {
  const b = (await banksList())[0];
  if (!b) throw new Error("There is no bank account in the accounting system yet.");
  return N(b.bankAccountId);
};
/** accounting statuses -> the page's: Draft / Posted / Paid / Void */
const docStat = (st: string): string => ({ Draft: "Draft", Unpaid: "Posted", PartPaid: "Posted", Paid: "Paid", Disputed: "Posted", Cancelled: "Void" } as Record<string, string>)[st] ?? st;

/* ================= chart of accounts ================= */
const coa: Binding = {
  load: async () => {
    const [list, groups] = await Promise.all([accountList(), arr("api/dashboard/ledger/groups")]);
    void groups;
    return list.map((a): Row => ({ id: S(a.code), _gid: N(a.accountId), account: S(a.code), name: S(a.name), type: acctType(a.type), balance: N(a.balance) }));
  },
  create: async (v) => {
    const groups = await arr("api/dashboard/ledger/groups");
    const g = groups.find((x) => acctType(x.type) === S(v.type));
    if (!g) throw new Error(`There is no account group for type ${S(v.type)} in the accounting system.`);
    await call("POST", "api/ledger/accounts", { groupId: N(g.groupId), accountCode: S(v.account), accountName: S(v.name), parentAccountId: null, currencyCode: null, isControl: false, isBank: false, isMonetaryFx: false, allowDirectPosting: true, description: null });
  },
  update: async (r, v) => void (await call("PUT", `api/ledger/accounts/${r._gid}`, { accountName: S(v.name), groupId: null, parentAccountId: null, clearParent: false, currencyCode: null, isControl: null, isBank: null, isMonetaryFx: null, allowDirectPosting: null, description: null })),
  remove: async (r) => void (await call("POST", `api/ledger/accounts/${r._gid}/deactivate`)),
};

/* ================= journals ================= */
const journal: Binding = {
  load: async () => {
    const list = items(await call<unknown>("GET", "api/ledger/journals?page=1&pageSize=200"));
    const full = await Promise.all(list.slice(0, 100).map((e) => call<Rec>("GET", `api/ledger/journals/${e.entryId}`).catch(() => e)));
    return full.map((e): Row => {
      const ls = (Array.isArray(e.lines) ? e.lines : []) as Rec[];
      const dr = ls.find((l) => N(l.debit) > 0);
      const cr = ls.find((l) => N(l.credit) > 0);
      return { id: S(e.referenceNumber), _gid: N(e.entryId), date: D(e.entryDate), description: S(e.description), debit: S(dr?.accountCode), credit: S(cr?.accountCode), amount: N(e.totalDebit), status: S(e.status) };
    });
  },
  create: async (v) => void (await call("POST", "api/ledger/journals", await journalBody(v))),
  update: async (r, v) => void (await call("PUT", `api/ledger/journals/${r._gid}`, await journalBody(v))),
  remove: async (r) => call<void>("DELETE", `api/ledger/journals/${r._gid}`),
  act: { Post: async (r) => (await call("POST", `api/ledger/journals/${r._gid}/post`), `${r.id} posted to the ledger`) },
};
async function journalBody(v: Vals): Promise<unknown> {
  const line = { costCentreId: null, projectId: null, taxCodeId: null, taxAmount: 0, currencyCode: null, exchangeRate: null, fcDebit: 0, fcCredit: 0, dimensions: null };
  return {
    entryDate: S(v.date), entryTypeId: 1, description: S(v.description), currencyCode: null, exchangeRate: null, referenceNumber: null, sourceModule: null, sourceReference: null,
    lines: [
      { accountId: await acctId(v.debit), debit: N(v.amount), credit: 0, description: S(v.description), ...line },
      { accountId: await acctId(v.credit), debit: 0, credit: N(v.amount), description: S(v.description), ...line },
    ],
  };
}

/* ================= customer invoices ================= */
const arInvoices: Binding = {
  load: async () => (await all("api/receivables/invoices")).map((e): Row => ({ id: S(e.invoiceNumber), _gid: N(e.invoiceId), customer: S(e.customerName), date: D(e.invoiceDate), due: D(e.dueDate), amount: N(e.netAmount), status: docStat(S(e.status)), _bal: N(e.balanceDue) })),
  create: async (v) => void (await call("POST", "api/receivables/invoices", await arBody(v))),
  update: async (r, v) => void (await call("PUT", `api/receivables/invoices/${r._gid}`, await arBody(v))),
  remove: async (r) => call<void>("DELETE", `api/receivables/invoices/${r._gid}`),
  act: {
    Post: async (r) => (await call("POST", `api/receivables/invoices/${r._gid}/post`, { overrideCreditLimit: false }), `${r.id} posted to receivables`),
    "Receive payment": async (r) => {
      const amt = N(r._bal) || N(r.amount);
      await call("POST", "api/receivables/receipts", { customerId: await customerId(r.customer), receiptDate: today(), paymentMethod: "Bank Transfer", amount: amt, reference: r.id, bankAccountId: await firstBank(), allocations: [{ invoiceId: N(r._gid), amountApplied: amt }], autoApply: false });
      return `${amt.toLocaleString()} received for ${r.id}`;
    },
  },
};
async function arBody(v: Vals): Promise<unknown> {
  return {
    customerId: await customerId(v.customer), invoiceDate: S(v.date), dueDate: S(v.due), currencyCode: null, exchangeRate: null, notes: null, soId: null, invoiceNumber: null,
    lines: [{ itemId: null, description: "Services", quantity: 1, unitPrice: N(v.amount), discountPercent: 0, taxCodeId: null, revenueAccountId: await acctId(v.revenue), costCentreId: null, projectId: null, soLineId: null, obligationId: null }],
  };
}

/* ================= vendor invoices ================= */
const apInvoices: Binding = {
  load: async () => (await all("api/payables/invoices")).map((e): Row => ({ id: S(e.invoiceNumber), _gid: N(e.invoiceId), vendor: S(e.vendorName), date: D(e.invoiceDate), due: D(e.dueDate), amount: N(e.netAmount), status: docStat(S(e.status)), _bal: N(e.balanceDue) })),
  create: async (v) => void (await call("POST", "api/payables/invoices", await apBody(v, ""))),
  update: async (r, v) => void (await call("PUT", `api/payables/invoices/${r._gid}`, await apBody(v, r.id))),
  remove: async (r) => call<void>("DELETE", `api/payables/invoices/${r._gid}`),
  act: {
    Post: async (r) => (await call("POST", `api/payables/invoices/${r._gid}/post`, { overrideMatch: false }), `${r.id} posted to payables`),
    Pay: async (r) => {
      const amt = N(r._bal) || N(r.amount);
      await call("POST", "api/payables/payments", { vendorId: await vendorId(r.vendor), paymentDate: today(), paymentMethod: "Bank Transfer", amount: amt, reference: r.id, bankAccountId: await firstBank(), allocations: [{ invoiceId: N(r._gid), amountApplied: amt, withholdingTaxCodeId: null, takeEarlyDiscount: false }], autoApply: false });
      return `${amt.toLocaleString()} paid on ${r.id}`;
    },
  },
};
async function apBody(v: Vals, number: string): Promise<unknown> {
  return {
    vendorId: await vendorId(v.vendor), invoiceNumber: number || `BILL-${Date.now().toString(36).toUpperCase()}`, invoiceDate: S(v.date), dueDate: S(v.due), currencyCode: null, exchangeRate: null, notes: null, poId: null,
    lines: [{ description: "Services", quantity: 1, unitPrice: N(v.amount), taxCodeId: null, expenseAccountId: await acctId(v.expense), costCentreId: null, projectId: null, poLineId: null }],
  };
}

/* ================= bank accounts ================= */
const banks: Binding = {
  load: async () => (await banksList()).map((b): Row => ({ id: `BK-${pad(N(b.bankAccountId))}`, _gid: N(b.bankAccountId), name: S(b.name), bank: S(b.bank), accountNo: S(b.accountNumber), currency: S(b.currencyCode) || "NGN", balance: N(b.balance), status: "Active" })),
  create: async (v) => {
    const used = new Set((await banksList()).map((b) => N(b.ledgerAccountId)));
    const free = (await accountList()).find((a) => a.isBank && !used.has(N(a.accountId)));
    if (!free) throw new Error("Create a ledger account marked as a bank account first (Chart of accounts), then add the bank account.");
    await call("POST", "api/dashboard/bank-accounts", { ledgerAccountId: N(free.accountId), accountName: S(v.name), bankName: S(v.bank), accountNumber: S(v.accountNo) || null });
  },
  update: async (r, v) => void (await call("PUT", `api/dashboard/bank-accounts/${r._gid}`, { ledgerAccountId: null, accountName: S(v.name), bankName: S(v.bank), accountNumber: S(v.accountNo) || null })),
  remove: async (r) => call<void>("DELETE", `api/dashboard/bank-accounts/${r._gid}`),
};

/* ================= tax rates ================= */
let taxRaw: Record<string, Rec> = {};
const taxes: Binding = {
  load: async () => {
    const list = await all("api/tax/codes");
    taxRaw = {};
    return list.map((t): Row => {
      taxRaw[S(t.code)] = t;
      return { id: S(t.code), _gid: N(t.taxCodeId), code: S(t.code), name: S(t.description), type: S(t.taxType), rate: N(t.rate), account: S(t.taxAccountCode) || S(t.taxAccountId), status: t.isActive ? "Active" : "Inactive" };
    });
  },
  create: async (v) => void (await call("POST", "api/tax/codes", { taxCodeId: 0, code: S(v.code).trim().toUpperCase(), description: S(v.name), taxType: S(v.type), rate: N(v.rate), taxAccountId: await acctId(v.account), isInclusive: false, effectiveFrom: today(), effectiveTo: null, isActive: true })),
  update: async (r, v) => void (await call("PUT", `api/tax/codes/${r._gid}`, { ...(taxRaw[r.id] ?? {}), description: S(v.name), taxType: S(v.type), rate: N(v.rate) })),
};

/* ================= fixed assets ================= */
const assets: Binding = {
  load: async () => {
    const [list, cats] = await Promise.all([all("api/assets/register"), all("api/assets/categories")]);
    const cat = new Map(cats.map((c) => [N(c.categoryId), c]));
    return list.map((a): Row => {
      const c = cat.get(N(a.categoryId));
      return { id: S(a.assetCode), _gid: N(a.assetId), name: S(a.assetName), group: S(c?.categoryName), acquired: D(a.acquisitionDate), cost: N(a.acquisitionCost), accum: N(a.accumulatedDepreciation), nbv: N(a.netBookValue), life: Math.round((N(c?.usefulLifeMonths) / 12) * 10) / 10, status: S(a.status) };
    });
  },
  create: async (v) => {
    const cat = (await all("api/assets/categories")).find((c) => S(c.categoryName) === S(v.group));
    if (!cat) throw new Error(`Asset category ${S(v.group)} does not exist in the accounting system.`);
    await call("POST", "api/assets/register", { categoryId: N(cat.categoryId), assetCode: null, assetName: S(v.name), acquisitionDate: S(v.acquired), acquisitionCost: N(v.cost), residualValue: null, location: null, serialNumber: null, costCentreId: null, projectId: null, parentAssetId: null, tagNumber: null, inServiceDate: null, methodOverride: null, usefulLifeMonthsOverride: N(v.life) > 0 ? Math.round(N(v.life) * 12) : null, unitsTotal: null, supplier: null, offsetAccountCode: null, postAcquisition: false });
  },
  update: async (r, v) => void (await call("PUT", `api/assets/register/${r._gid}`, { assetName: S(v.name), location: null, serialNumber: null, costCentreId: null, projectId: null, tagNumber: null, supplier: null, warrantyUntil: null })),
  act: {
    "Run depreciation": async () => {
      const p = await call<Rec>("GET", "api/ledger/periods/current");
      const res = await call<Rec>("POST", "api/assets/depreciation/run", { periodId: N(p.periodId), dryRun: false, units: null });
      return `Depreciation posted for ${S(res.periodName) || "the current period"}`;
    },
  },
};

/* ================= budgets (set in Planning; read here) ================= */
const budgets: Binding = {
  load: async () => {
    const r = await call<{ lines?: Rec[] }>("GET", "api/dashboard/budget-tracking?period=year");
    return (r.lines ?? []).map((l): Row => ({ id: S(l.accountCode), department: "", account: S(l.accountCode), budget: N(l.budget), actual: N(l.actual), variance: N(l.budget) - N(l.actual), used: N(l.usedPercent), status: S(l.status) }));
  },
  noCreate: "Budgets are set in Planning (Budget versions).",
};

/* ================= direct expenses (read; posting happens in the accounting screen) ================= */
const expenses: Binding = {
  load: async () => (await all("api/dashboard/expenses")).map((e): Row => ({ id: S(e.expenseNumber), _gid: N(e.expenseId), employee: S(e.payee), purpose: S(e.memo), category: S(e.accountId), date: D(e.expenseDate), amount: N(e.amount), status: S(e.status) })),
  remove: async (r) => call<void>("DELETE", `api/dashboard/expenses/${r._gid}`),
  noCreate: "Record expenses from the accounting screen (they need a bank account and ledger account).",
  act: { Reimburse: async (r) => (await call("POST", `api/dashboard/expenses/${r._gid}/post`), `${r.id} posted`) },
};

/* ================= fiscal periods ================= */
const periods: Binding = {
  load: async () => {
    const years = await arr("api/ledger/periods/years");
    const out: Row[] = [];
    for (const y of years) {
      const full = Array.isArray(y.periods) ? y : await call<Rec>("GET", `api/ledger/periods/years/${y.fiscalYearId}`).catch(() => y);
      for (const p of ((full.periods as Rec[]) ?? [])) out.push({ id: S(p.periodName), _gid: N(p.periodId), name: S(p.periodName), start: D(p.startDate), end: D(p.endDate), status: S(p.status) });
    }
    return out;
  },
  noCreate: "Fiscal years and periods are generated in the accounting system.",
};

export const ACC_BINDINGS: Record<string, Binding> = { coa, journal, ar_invoices: arInvoices, ap_invoices: apInvoices, banks, taxes, assets, budgets, expenses, periods };
export const ACC_IDS = Object.keys(ACC_BINDINGS);